import type { Prisma, PrismaClient } from '@prisma/client';
import { Router, type Request } from 'express';
import {
  PERMISSION_CATALOGUE,
  expandPermissions,
  isKnownPermission,
  normalisePhone,
} from '@roovers/shared';
import { z } from 'zod';
import { audit } from '../../core/audit.js';
import type { Config } from '../../core/config.js';
import { randomToken, sha256 } from '../../core/crypto.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler, clientIp, pageParams, parse } from '../../core/http.js';
import { idempotent } from '../../core/idempotency.js';
import { authenticate, permissionsGranted, requirePermission } from '../auth/auth.middleware.js';
import { INVITE_TTL_MS } from '../auth/auth.routes.js';
import { revokeAllSessions } from '../auth/tokens.js';
import { queueEmail, renderTemplate } from '../messaging/messaging.service.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

const OWNER_ROLE = 'Owner';

export function createTeamRouter({ db, config }: Deps) {
  const router = Router();
  // Scoped to this router's own paths so unrelated routes (cron, 404s) are not intercepted.
  router.use(['/users', '/roles'], authenticate({ db, config }), idempotent(db));

  const actor = (req: Request) => ({
    companyId: req.auth!.companyId,
    userId: req.auth!.userId,
    ip: clientIp(req),
  });

  /** Active Owners other than `excludeUserId`. Used to protect the last Owner. */
  async function otherActiveOwners(
    tx: Prisma.TransactionClient | PrismaClient,
    companyId: number,
    excludeUserId: number,
  ) {
    return tx.user.count({
      where: {
        companyId,
        id: { not: excludeUserId },
        status: 'active',
        roles: { some: { role: { name: OWNER_ROLE } } },
      },
    });
  }

  const userSelect = {
    id: true,
    email: true,
    name: true,
    phone: true,
    status: true,
    twoFactorEnabled: true,
    lastLoginAt: true,
    createdAt: true,
    roles: { select: { role: { select: { id: true, name: true } } } },
  } satisfies Prisma.UserSelect;

  const shapeUser = (u: Prisma.UserGetPayload<{ select: typeof userSelect }>) => ({
    id: u.id,
    email: u.email,
    name: u.name,
    phone: u.phone,
    status: u.status,
    twoFactorEnabled: u.twoFactorEnabled,
    lastLoginAt: u.lastLoginAt,
    createdAt: u.createdAt,
    roles: u.roles.map((r) => r.role),
  });

  async function assertCanAssignRoles(req: Request, roleIds: number[]) {
    const roles = await db.role.findMany({
      where: { id: { in: roleIds }, companyId: req.auth!.companyId },
      include: { permissions: true },
    });
    if (roles.length !== new Set(roleIds).size)
      throw new HttpError(400, 'UNKNOWN_ROLE', 'One or more roles do not exist');
    const isOwner = permissionsGranted(req.auth!.permissions, '*');
    for (const role of roles) {
      if (role.name === OWNER_ROLE && !isOwner) {
        throw new HttpError(403, 'FORBIDDEN', 'Only an Owner can grant the Owner role');
      }
      // No privilege escalation: you cannot hand out permissions you do not hold.
      if (!isOwner) {
        const missing = role.permissions.find(
          (p) => !permissionsGranted(req.auth!.permissions, p.permission),
        );
        if (missing)
          throw new HttpError(
            403,
            'FORBIDDEN',
            `You cannot assign a role with permissions you lack (${missing.permission})`,
          );
      }
    }
    return roles;
  }

  // ───── users ─────
  router.get(
    '/users',
    requirePermission('team.user.view'),
    asyncHandler(async (req, res) => {
      const { page, pageSize, skip, take } = pageParams(req.query as Record<string, unknown>);
      const search = typeof req.query.search === 'string' ? req.query.search.trim() : '';
      const where: Prisma.UserWhereInput = {
        companyId: req.auth!.companyId,
        ...(search
          ? { OR: [{ name: { contains: search } }, { email: { contains: search } }] }
          : {}),
      };
      const [total, rows] = await Promise.all([
        db.user.count({ where }),
        db.user.findMany({ where, select: userSelect, orderBy: { name: 'asc' }, skip, take }),
      ]);
      res.json({ items: rows.map(shapeUser), total, page, pageSize });
    }),
  );

  const inviteSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(191),
    name: z.string().trim().min(1).max(200),
    phone: z.string().trim().max(30).optional(),
    roleIds: z.array(z.number().int().positive()).min(1).max(10),
  });

  async function sendInvite(userId: number, companyId: number, sentById: number) {
    const user = await db.user.findUniqueOrThrow({ where: { id: userId } });
    const company = await db.company.findUniqueOrThrow({ where: { id: companyId } });
    const token = randomToken(32);
    await db.userToken.create({
      data: {
        userId,
        type: 'invite',
        tokenHash: sha256(token),
        expiresAt: new Date(Date.now() + INVITE_TTL_MS),
      },
    });
    const link = `${config.APP_URL}/accept-invite?token=${token}`;
    await queueEmail(db, {
      companyId,
      to: [user.email],
      subject: `You have been invited to ${company.name}`,
      text: renderTemplate(
        'Hello {{user_name}},\n\nYou have been invited to {{company_name}} on Roovers Business Suite. Set your password here (valid for 7 days):\n{{invite_link}}\n\n{{company_name}}',
        { user_name: user.name, company_name: company.name, invite_link: link },
      ),
      templateName: 'invitation',
      sentById,
    });
    return link;
  }

  router.post(
    '/users/invite',
    requirePermission('team.user.create'),
    asyncHandler(async (req, res) => {
      const body = parse(inviteSchema, req.body);
      const phone = body.phone ? normalisePhone(body.phone) : null;
      if (body.phone && !phone)
        throw new HttpError(400, 'INVALID_PHONE', 'Enter a valid phone number');
      await assertCanAssignRoles(req, body.roleIds);

      const exists = await db.user.findUnique({
        where: { companyId_email: { companyId: req.auth!.companyId, email: body.email } },
      });
      if (exists) throw new HttpError(409, 'EMAIL_TAKEN', 'A user with this email already exists');

      const user = await db.$transaction(async (tx) => {
        const created = await tx.user.create({
          data: {
            companyId: req.auth!.companyId,
            email: body.email,
            name: body.name,
            phone,
            status: 'invited',
            createdBy: req.auth!.userId,
            roles: { create: body.roleIds.map((roleId) => ({ roleId })) },
          },
          select: userSelect,
        });
        await audit(tx, actor(req), {
          action: 'user.invited',
          entity: 'user',
          entityId: created.id,
          after: { email: created.email, roles: created.roles.map((r) => r.role.name) },
        });
        return created;
      });
      const link = await sendInvite(user.id, req.auth!.companyId, req.auth!.userId);
      // Outside production the link is returned so the flow can be tried without a mail server.
      res.status(201).json({
        ...shapeUser(user),
        ...(config.NODE_ENV !== 'production' ? { inviteLink: link } : {}),
      });
    }),
  );

  router.post(
    '/users/:id/resend-invite',
    requirePermission('team.user.create'),
    asyncHandler(async (req, res) => {
      const user = await db.user.findFirst({
        where: { id: Number(req.params.id), companyId: req.auth!.companyId },
      });
      if (!user) throw new HttpError(404, 'NOT_FOUND', 'User not found');
      if (user.status !== 'invited')
        throw new HttpError(409, 'NOT_INVITED', 'This user has already accepted');
      const link = await sendInvite(user.id, user.companyId, req.auth!.userId);
      res.json({ ok: true, ...(config.NODE_ENV !== 'production' ? { inviteLink: link } : {}) });
    }),
  );

  const updateUserSchema = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    phone: z.string().trim().max(30).nullable().optional(),
    status: z.enum(['active', 'suspended']).optional(),
    roleIds: z.array(z.number().int().positive()).min(1).max(10).optional(),
  });

  router.patch(
    '/users/:id',
    requirePermission('team.user.edit'),
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const body = parse(updateUserSchema, req.body);
      const target = await db.user.findFirst({
        where: { id, companyId: req.auth!.companyId },
        select: userSelect,
      });
      if (!target) throw new HttpError(404, 'NOT_FOUND', 'User not found');

      const targetIsOwner = target.roles.some((r) => r.role.name === OWNER_ROLE);
      if (targetIsOwner && !permissionsGranted(req.auth!.permissions, '*')) {
        throw new HttpError(403, 'FORBIDDEN', 'Only an Owner can change an Owner');
      }
      if (body.roleIds) await assertCanAssignRoles(req, body.roleIds);

      let phone: string | null | undefined;
      if (body.phone !== undefined) {
        phone = body.phone ? normalisePhone(body.phone) : null;
        if (body.phone && !phone)
          throw new HttpError(400, 'INVALID_PHONE', 'Enter a valid phone number');
      }

      const ownerRoleId = (
        await db.role.findUnique({
          where: { companyId_name: { companyId: req.auth!.companyId, name: OWNER_ROLE } },
        })
      )?.id;
      const losesOwner =
        targetIsOwner &&
        target.status === 'active' &&
        ((body.status && body.status !== 'active') ||
          (body.roleIds && ownerRoleId && !body.roleIds.includes(ownerRoleId)));

      const updated = await db.$transaction(async (tx) => {
        if (losesOwner && (await otherActiveOwners(tx, req.auth!.companyId, id)) === 0) {
          throw new HttpError(409, 'LAST_OWNER', 'There must always be at least one active Owner');
        }
        if (body.roleIds) {
          await tx.userRole.deleteMany({ where: { userId: id } });
          await tx.userRole.createMany({
            data: body.roleIds.map((roleId) => ({ userId: id, roleId })),
          });
        }
        const user = await tx.user.update({
          where: { id },
          data: {
            ...(body.name !== undefined ? { name: body.name } : {}),
            ...(phone !== undefined ? { phone } : {}),
            ...(body.status ? { status: body.status } : {}),
            updatedBy: req.auth!.userId,
          },
          select: userSelect,
        });
        await audit(tx, actor(req), {
          action: 'user.updated',
          entity: 'user',
          entityId: id,
          before: shapeUser(target),
          after: shapeUser(user),
        });
        return user;
      });

      if (body.status === 'suspended' || body.roleIds) await revokeAllSessions(db, id);
      res.json(shapeUser(updated));
    }),
  );

  router.post(
    '/users/:id/sign-out',
    requirePermission('team.user.edit'),
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const target = await db.user.findFirst({ where: { id, companyId: req.auth!.companyId } });
      if (!target) throw new HttpError(404, 'NOT_FOUND', 'User not found');
      await revokeAllSessions(db, id);
      await audit(db, actor(req), { action: 'user.forced_sign_out', entity: 'user', entityId: id });
      res.status(204).end();
    }),
  );

  router.post(
    '/users/:id/reset-2fa',
    requirePermission('team.user.edit'),
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const target = await db.user.findFirst({
        where: { id, companyId: req.auth!.companyId },
        include: { roles: { include: { role: true } } },
      });
      if (!target) throw new HttpError(404, 'NOT_FOUND', 'User not found');
      if (
        target.roles.some((r) => r.role.name === OWNER_ROLE) &&
        !permissionsGranted(req.auth!.permissions, '*')
      ) {
        throw new HttpError(403, 'FORBIDDEN', 'Only an Owner can reset an Owner’s two-factor');
      }
      await db.$transaction(async (tx) => {
        await tx.userTotp.deleteMany({ where: { userId: id } });
        await tx.user.update({ where: { id }, data: { twoFactorEnabled: false } });
        await audit(tx, actor(req), { action: 'user.2fa_reset', entity: 'user', entityId: id });
      });
      await revokeAllSessions(db, id);
      res.status(204).end();
    }),
  );

  // ───── roles ─────
  router.get('/roles/catalogue', requirePermission('team.role.view'), (_req, res) => {
    res.json({ catalogue: PERMISSION_CATALOGUE });
  });

  const shapeRole = (
    r: Prisma.RoleGetPayload<{
      include: { permissions: true; _count: { select: { users: true } } };
    }>,
  ) => ({
    id: r.id,
    name: r.name,
    description: r.description,
    isSystem: r.isSystem,
    requiresTwoFactor: r.requiresTwoFactor,
    scope: r.scope,
    approvalLimit: r.approvalLimit == null ? null : r.approvalLimit.toString(),
    permissions: r.permissions.map((p) => p.permission).sort(),
    userCount: r._count.users,
  });

  router.get(
    '/roles',
    requirePermission('team.role.view'),
    asyncHandler(async (req, res) => {
      const roles = await db.role.findMany({
        where: { companyId: req.auth!.companyId },
        include: { permissions: true, _count: { select: { users: true } } },
        orderBy: { id: 'asc' },
      });
      res.json({ items: roles.map(shapeRole) });
    }),
  );

  const roleSchema = z.object({
    name: z.string().trim().min(2).max(100),
    description: z.string().trim().max(300).optional(),
    permissions: z.array(z.string()).max(500),
    scope: z.enum(['own', 'all']).default('all'),
    requiresTwoFactor: z.boolean().default(false),
    approvalLimit: z.number().nonnegative().nullable().default(null),
  });

  function checkedPermissions(req: Request, requested: string[]) {
    const expanded = expandPermissions(requested);
    if (expanded.includes('*'))
      throw new HttpError(400, 'INVALID_PERMISSION', 'The Owner wildcard cannot be assigned');
    const unknown = requested.find(
      (p) => !isKnownPermission(p) && expandPermissions([p]).length === 0,
    );
    if (unknown) throw new HttpError(400, 'INVALID_PERMISSION', `Unknown permission: ${unknown}`);
    if (!permissionsGranted(req.auth!.permissions, '*')) {
      const missing = expanded.find((p) => !permissionsGranted(req.auth!.permissions, p));
      if (missing)
        throw new HttpError(
          403,
          'FORBIDDEN',
          `You cannot grant a permission you lack (${missing})`,
        );
    }
    return expanded;
  }

  router.post(
    '/roles',
    requirePermission('team.role.create'),
    asyncHandler(async (req, res) => {
      const body = parse(roleSchema, req.body);
      const permissions = checkedPermissions(req, body.permissions);
      const dup = await db.role.findUnique({
        where: { companyId_name: { companyId: req.auth!.companyId, name: body.name } },
      });
      if (dup) throw new HttpError(409, 'NAME_TAKEN', 'A role with this name already exists');
      const role = await db.$transaction(async (tx) => {
        const created = await tx.role.create({
          data: {
            companyId: req.auth!.companyId,
            name: body.name,
            description: body.description,
            scope: body.scope,
            requiresTwoFactor: body.requiresTwoFactor,
            approvalLimit: body.approvalLimit,
            permissions: { create: permissions.map((permission) => ({ permission })) },
          },
          include: { permissions: true, _count: { select: { users: true } } },
        });
        await audit(tx, actor(req), {
          action: 'role.created',
          entity: 'role',
          entityId: created.id,
          after: shapeRole(created),
        });
        return created;
      });
      res.status(201).json(shapeRole(role));
    }),
  );

  router.patch(
    '/roles/:id',
    requirePermission('team.role.edit'),
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const body = parse(roleSchema.partial(), req.body);
      const existing = await db.role.findFirst({
        where: { id, companyId: req.auth!.companyId },
        include: { permissions: true, _count: { select: { users: true } } },
      });
      if (!existing) throw new HttpError(404, 'NOT_FOUND', 'Role not found');
      if (existing.name === OWNER_ROLE)
        throw new HttpError(403, 'FORBIDDEN', 'The Owner role cannot be changed');
      if (existing.isSystem && body.name && body.name !== existing.name) {
        throw new HttpError(400, 'SYSTEM_ROLE', 'System roles cannot be renamed');
      }
      const permissions = body.permissions ? checkedPermissions(req, body.permissions) : undefined;

      const role = await db.$transaction(async (tx) => {
        if (permissions) {
          await tx.rolePermission.deleteMany({ where: { roleId: id } });
          await tx.rolePermission.createMany({
            data: permissions.map((permission) => ({ roleId: id, permission })),
          });
        }
        const updated = await tx.role.update({
          where: { id },
          data: {
            ...(body.name ? { name: body.name } : {}),
            ...(body.description !== undefined ? { description: body.description } : {}),
            ...(body.scope ? { scope: body.scope } : {}),
            ...(body.requiresTwoFactor !== undefined
              ? { requiresTwoFactor: body.requiresTwoFactor }
              : {}),
            ...(body.approvalLimit !== undefined ? { approvalLimit: body.approvalLimit } : {}),
          },
          include: { permissions: true, _count: { select: { users: true } } },
        });
        await audit(tx, actor(req), {
          action: 'role.updated',
          entity: 'role',
          entityId: id,
          before: shapeRole(existing),
          after: shapeRole(updated),
        });
        return updated;
      });
      res.json(shapeRole(role));
    }),
  );

  router.delete(
    '/roles/:id',
    requirePermission('team.role.delete'),
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const role = await db.role.findFirst({
        where: { id, companyId: req.auth!.companyId },
        include: { permissions: true, _count: { select: { users: true } } },
      });
      if (!role) throw new HttpError(404, 'NOT_FOUND', 'Role not found');
      if (role.isSystem) throw new HttpError(403, 'SYSTEM_ROLE', 'System roles cannot be deleted');
      if (role._count.users > 0)
        throw new HttpError(409, 'ROLE_IN_USE', 'Move the users to another role first');
      await db.$transaction(async (tx) => {
        await tx.role.delete({ where: { id } });
        await audit(tx, actor(req), {
          action: 'role.deleted',
          entity: 'role',
          entityId: id,
          before: shapeRole(role),
        });
      });
      res.status(204).end();
    }),
  );

  return router;
}
